// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Drives course-mapping capture from a round's lifecycle. Android only — every path
// below short-circuits elsewhere via courseTrace.isSupported().

import { useEffect, useRef } from 'react';

import {
  flushCourseTraces,
  isSupported,
  recordCourseTraceMarker,
  setCourseTraceHole,
  startCourseTrace,
  stopCourseTrace,
} from '@/services/courseTrace';
import {
  createTraceUploader,
  fetchCourseMappingStatus,
  resolveRoundCourseId,
} from '@/services/courseTraceUpload';
import { getBatteryFraction, getDeviceModel } from '@/services/deviceSignals';

import { getCourseTraceConsent } from './consent';

export type UseCourseTraceParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  roundId: string | null;
  holeNumber: number | null;
  /** Capture runs only while the round is in play. */
  isRoundActive: boolean;
};

export const useCourseTrace = ({
  apiBaseUrl,
  token,
  roundId,
  holeNumber,
  isRoundActive,
}: UseCourseTraceParams): void => {
  // Held in a ref so the start/stop effect does not re-run whenever the token refreshes
  // mid-round, which would stop and restart capture for no reason.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!isSupported() || !roundId || !isRoundActive) return undefined;

    let cancelled = false;

    const begin = async () => {
      // Consent first: nothing below runs, and no lookup is made, without it.
      if (!(await getCourseTraceConsent())) return;
      if (cancelled) return;

      const courseId = await resolveRoundCourseId(apiBaseUrl, roundId, tokenRef.current);
      if (cancelled || !courseId) return;

      const mappingStatus = await fetchCourseMappingStatus(apiBaseUrl, courseId, tokenRef.current);
      if (cancelled || mappingStatus === 'complete') return;

      await startCourseTrace({
        roundId,
        courseId,
        deviceModel: getDeviceModel(),
        hasConsent: true,
        mappingStatus,
        batteryFraction: await getBatteryFraction(),
      });
    };

    void begin();

    return () => {
      cancelled = true;
      // Sealing the buffer on the way out is what stops the last few holes of every
      // round being lost.
      void stopCourseTrace().then(() =>
        flushCourseTraces(createTraceUploader(apiBaseUrl, () => tokenRef.current))
      );
    };
  }, [apiBaseUrl, isRoundActive, roundId]);

  // Fixes carry the hole the player is on, so the server can bound attribution without
  // trusting where a marker fired.
  useEffect(() => {
    if (!isSupported()) return;
    setCourseTraceHole(holeNumber);
    if (holeNumber !== null) {
      recordCourseTraceMarker('hole.entered', holeNumber);
    }
  }, [holeNumber]);
};

/**
 * Records that a stroke was entered. Attribution only — this fires wherever the player
 * happened to enter the score, which is routinely the next tee.
 */
export const recordCourseTraceStroke = (holeNumber: number | null): void => {
  if (!isSupported()) return;
  recordCourseTraceMarker('stroke.recorded', holeNumber);
};
